import classNames from 'classnames';
import React from 'react';
import { ClipLoader } from 'react-spinners';
import { AsyncState } from 'shared/utils/async-state';
import { DivProps } from 'typings';

export interface UDAsyncContentProps extends Omit<DivProps, 'children'> {
  /** Состояние выдачи, посчитанное `deriveAsyncState`. */
  state: AsyncState;
  /** Чем заменить содержимое, пока показывать нечего. По умолчанию — спиннер. */
  loading?: React.ReactNode;
  /** Что показать, если запрос упал и данных нет. */
  error?: React.ReactNode;
  /** Что показать, если ответ пришёл и он пуст. */
  empty?: React.ReactNode;
  /**
   * Высота служебных веток. Без неё содержимое прыгает, когда спиннер сменяется
   * списком — единственные два места в проекте, где это учтено, задают её вручную
   * (`table.tsx`, `select-tournament.tsx`).
   */
  minHeight?: number | string;
  children: React.ReactNode;
}

/**
 * Единая раскладка состояний асинхронной выдачи: индикатор, ошибка, «пусто», данные.
 *
 * Отличие от привычного `if (isLoading) return <Skeleton/>` в том, что «идёт запрос»
 * не стирает уже показанное. Пока есть что показывать, рисуются `children` — просто
 * приглушённые и помеченные `aria-busy`, чтобы было видно: на экране прошлый ответ.
 * Ветку выбирает `state.status`, приглушение включает `state.isRefreshing`.
 *
 * Служебные ветки (`loading` / `error` / `empty`) — слоты: экран передаёт туда свой
 * скелетон или текст, общей остаётся только раскладка и правило переключения.
 *
 * Подробности и рецепты — `.claude/docs/async-loading-states.md`.
 */
const UDAsyncContent = ({
  state,
  loading,
  error,
  empty,
  minHeight,
  className,
  style,
  children,
  ...rest
}: UDAsyncContentProps) => {
  const { status, isRefreshing } = state;

  if (status !== 'ready') {
    const fallback = {
      // `currentColor` вместо цвета из темы: спиннер наследует цвет текста своего
      // окружения и одинаково читается на светлом и на тёмном фоне. Тот же приём,
      // что в `icon-button.tsx`.
      loading: loading ?? <ClipLoader color="currentColor" />,
      error,
      empty,
    }[status];

    return (
      <div
        {...rest}
        className={classNames('flex-center w-full', className)}
        style={{ minHeight, ...style }}
        role="status"
        aria-live="polite"
      >
        {fallback}
      </div>
    );
  }

  return (
    <div
      {...rest}
      className={classNames('transition-opacity', className, { 'pointer-events-none opacity-50': isRefreshing })}
      style={style}
      aria-busy={isRefreshing}
    >
      {children}
    </div>
  );
};

export default UDAsyncContent;
